import { useCallback, useEffect, useState } from "react";
import { Api, ApiError, type AppNotification, type ModelMigration } from "../api.js";
import { useI18n } from "../i18n.js";

type Active = Extract<ModelMigration, { active: true }>;

/**
 * The search-model change, for owners and managers (docs/MODEL-LIFECYCLE.md, section 13).
 * A prominent banner that stays until the person says "I have reviewed it" or switches: a small notification does not get
 * acknowledged. Shows nothing when there is no change, or when this person may not see it (members get a 403/404).
 */
/** Only an https address is ever made a link. */
const safeUrl = (u: string | null | undefined): string | null => {
  try {
    const x = new URL(u ?? "");
    return x.protocol === "https:" ? x.href : null;
  } catch {
    return null;
  }
};

export function ModelMigrationBanner({ api, agentId, onChanged, note }: { api: Api; agentId: string; onChanged?: () => void; note?: AppNotification }) {
  const { t, lang } = useI18n();
  const [m, setM] = useState<Active | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"" | "ack" | "switch" | "test">("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [fetched, setFetched] = useState<AppNotification | null>(null);
  const info = note ?? fetched;

  const apply = useCallback(
    (v: ModelMigration) => {
      setM(v.active ? v : null);
      onChanged?.();
    },
    [onChanged],
  );

  useEffect(() => {
    let live = true;
    api.modelMigration(agentId).then(
      (v) => live && setM(v.active ? v : null),
      () => live && setM(null),
    );
    return () => {
      live = false;
    };
  }, [api, agentId]);

  // The names, dates and the provider's notice come with the notification; the agent screen asks for it too.
  useEffect(() => {
    if (note) return;
    let live = true;
    api.notifications(168).then(
      (r) => live && setFetched(r.notifications.find((n) => n.actionRequired?.agentId === agentId) ?? null),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [api, agentId, note]);

  if (!m) return null;
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : "Something went wrong. Try again.");

  const acknowledge = async () => {
    setBusy("ack");
    setError(null);
    try {
      apply(await api.acknowledgeModelMigration(agentId));
    } catch (e) {
      fail(e);
    } finally {
      setBusy("");
    }
  };
  const switchNow = async () => {
    setBusy("switch");
    setError(null);
    try {
      apply(await api.switchModelMigration(agentId));
    } catch (e) {
      fail(e);
    } finally {
      setBusy("");
    }
  };
  const test = async () => {
    if (!question.trim()) return;
    setBusy("test");
    setError(null);
    setAnswer(null);
    try {
      setAnswer((await api.testWithNewModel(agentId, question.trim())).text);
      const v = await api.modelMigration(agentId);
      if (v.active) setM(v);
    } catch (e) {
      fail(e);
    } finally {
      setBusy("");
    }
  };

  if (m.switched) {
    return (
      <section className="banner ok" role="status" aria-label={t("mm.switchedTitle")} data-testid="model-migration">
        <p><strong>{t("mm.switchedTitle")}</strong></p>
        <p>{t("mm.switchedBody")}</p>
        {m.undoUntil && <p className="hint">{t("mm.undo", { date: new Date(m.undoUntil).toLocaleDateString(lang) })}</p>}
      </section>
    );
  }

  const deadline =
    m.daysLeft === null ? null : m.daysLeft < 0 ? t("mm.pastDue") : m.daysLeft === 0 ? t("mm.daysToday") : m.daysLeft === 1 ? t("mm.daysOne") : t("mm.days", { n: String(m.daysLeft) });
  const progress = m.progress && m.progress.total > 0 ? t("mm.preparing", { done: String(m.progress.done), total: String(m.progress.total) }) : t("mm.preparingNoCount");

  const d = info?.actionRequired;
  const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(lang) : null);
  const notice = safeUrl(d?.noticeUrl);
  const details = d && (
    <div className="card" style={{ margin: ".5rem 0" }} data-testid="model-migration-details">
      {info?.message && <p>{info.message}</p>}
      {info?.advice && <p className="hint">{info.advice}</p>}
      {(d.currentModel || d.newModel) && (
        <dl className="facts">
          {d.currentModel && (<><dt>{t("mm.current")}</dt><dd>{d.currentModel}</dd></>)}
          {d.newModel && (<><dt>{t("mm.new")}</dt><dd>{d.newModel}</dd></>)}
          {when(d.retiresAt) && (<><dt>{t("mm.retires")}</dt><dd>{when(d.retiresAt)}</dd></>)}
          {when(d.switchesOn) && (<><dt>{t("mm.switchesOn")}</dt><dd>{when(d.switchesOn)}</dd></>)}
        </dl>
      )}
      <p>
        {notice ? (
          <a href={notice} target="_blank" rel="noopener noreferrer">
            {t("mm.notice")}
            <span className="sr-only"> {t("mm.newTab")}</span>
          </a>
        ) : (
          <span className="hint">{t("mm.noNotice")}</span>
        )}
      </p>
    </div>
  );

  const actions = (
    <div className="row">
      {m.canSwitch && (
        <button className="btn primary" disabled={busy !== ""} onClick={() => void switchNow()}>
          {busy === "switch" ? t("mm.switching") : t("mm.switch")}
        </button>
      )}
      {!m.acknowledged && (
        <button className="btn secondary" disabled={busy !== ""} onClick={() => void acknowledge()}>
          {t("mm.reviewed")}
        </button>
      )}
    </div>
  );

  const tester = m.ready ? (
    <div className="card" style={{ margin: ".5rem 0" }}>
      <strong>{t("mm.testTitle")}</strong>
      <p className="hint">{t("mm.testHint")}</p>
      <label className="inline">
        <span className="sr-only">{t("mm.testLabel")}</span>
        <input
          type="text"
          aria-label={t("mm.testLabel")}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void test()}
          style={{ width: "100%" }}
        />
      </label>
      <button className="btn secondary" disabled={busy !== "" || !question.trim()} onClick={() => void test()}>
        {busy === "test" ? t("mm.testing") : t("mm.testButton")}
      </button>
      {answer !== null && (
        <p role="status">
          <strong>{t("mm.answer")}</strong> {answer}
        </p>
      )}
      {m.tested && <p className="hint">{t("mm.tested")}</p>}
    </div>
  ) : (
    <p className="hint">{t("mm.notReady")}</p>
  );

  // Reviewed but not switched: a quieter reminder that still offers the actions.
  if (m.acknowledged) {
    return (
      <section className="banner" role="status" aria-label={t("mm.ackedTitle")} data-testid="model-migration">
        <p><strong>{t("mm.ackedTitle")}</strong></p>
        <p>{t("mm.ackedBody")}</p>
        {details}
        {deadline && <p className="hint">{deadline}</p>}
        {!m.ready && <p className="hint">{progress}</p>}
        {tester}
        {error && <p role="alert">{error}</p>}
        {actions}
      </section>
    );
  }

  return (
    <section className="banner warn" role="alert" aria-label={t("mm.title")} data-testid="model-migration">
      <p><strong>{t("mm.title")}</strong></p>
      <p>{t("mm.body")}</p>
      {details}
      {deadline && <p><strong>{deadline}</strong></p>}
      {!m.ready && <p>{progress}</p>}
      {tester}
      {error && <p role="alert">{error}</p>}
      {actions}
    </section>
  );
}
